import {
  useInfiniteQuery as useInfiniteQueryFromTanstackReactQuery,
  useQueries as useQueriesFromTanstackReactQuery,
  useQuery as useQueryFromTanstackReactQuery,
  useSuspenseInfiniteQuery as useSuspenseInfiniteQueryFromTanstackReactQuery,
} from "@tanstack/react-query";
import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { createContext, useContext, useMemo, useSyncExternalStore } from "react";

/** A bounded prepared route must resolve cold queries before native Back can reveal it. */
export const NavigationQueryActivityContext = createContext(false);

type SubscribableOptions = {
  subscribed?: boolean;
};

/**
 * How long a blur waits before it is published to React.
 *
 * Every `useQuery` call site subscribes to its screen's focus, so publishing a blur re-renders
 * every query consumer of the screen being LEFT — on a bottom-tab switch that is a whole tab's
 * worth of components, rendered in the same burst as the tab being entered, for a screen nobody
 * can see any more. Nothing about a blur is urgent: it only stops future notifications. So it is
 * published after the switch has settled, and not at all when the screen is focused again first
 * (tab A → B → A). For that window a hidden screen can still take one update, which is harmless.
 * A FOCUS is always published immediately: the screen being entered must resubscribe and catch up.
 */
const BLUR_PUBLISH_DELAY_MS = 400;

type NavigationFocusSource = {
  addListener: (event: "blur" | "focus", listener: () => void) => () => void;
  isFocused: () => boolean;
};

function createNavigationFocusStore(navigation: NavigationFocusSource | undefined) {
  let isFocused = navigation?.isFocused() ?? true;
  return {
    getSnapshot: () => isFocused,
    subscribe: (onStoreChange: () => void) => {
      if (navigation === undefined) {
        return () => {};
      }
      let blurTimer: ReturnType<typeof setTimeout> | undefined;
      const publish = (next: boolean) => {
        if (isFocused !== next) {
          isFocused = next;
          onStoreChange();
        }
      };
      const cancelPendingBlur = () => {
        clearTimeout(blurTimer);
        blurTimer = undefined;
      };
      const unsubscribeFocus = navigation.addListener("focus", () => {
        cancelPendingBlur();
        publish(true);
      });
      const scheduleBlur = () => {
        cancelPendingBlur();
        blurTimer = setTimeout(() => {
          blurTimer = undefined;
          publish(navigation.isFocused());
        }, BLUR_PUBLISH_DELAY_MS);
      };
      const unsubscribeBlur = navigation.addListener("blur", scheduleBlur);
      // Focus can have changed between the render that read the snapshot and this subscription.
      if (navigation.isFocused()) {
        publish(true);
      } else {
        scheduleBlur();
      }
      return () => {
        cancelPendingBlur();
        unsubscribeFocus();
        unsubscribeBlur();
      };
    },
  };
}

/**
 * React Navigation's `useIsFocused`, with two deliberate differences: components outside a
 * navigation container are considered focused (React Query is also used by root providers, SSR,
 * and isolated tests, where calling `useIsFocused` directly would throw), and a blur is published
 * late — see {@link BLUR_PUBLISH_DELAY_MS}.
 */
function useOptionalIsFocused(subscribed: boolean | undefined): boolean {
  const contextNavigation = useContext(NavigationContext);
  const isPreparedBody = useContext(NavigationQueryActivityContext);
  const navigation = subscribed === undefined && !isPreparedBody ? contextNavigation : undefined;
  const store = useMemo(() => createNavigationFocusStore(navigation), [navigation]);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * Preserve option getters when adding the default. Several Han query option objects intentionally
 * expose `queryKey` as a getter, so a normal object spread here would snapshot and break that key.
 */
function withNavigationFocusSubscription<TOptions extends SubscribableOptions>(
  options: TOptions,
  isFocused: boolean,
): TOptions {
  const descriptors = Object.getOwnPropertyDescriptors(options);
  descriptors.subscribed = {
    configurable: true,
    enumerable: true,
    value: options.subscribed ?? isFocused,
    writable: true,
  };

  return Object.create(Object.getPrototypeOf(options), descriptors) as TOptions;
}

export const useQuery: typeof useQueryFromTanstackReactQuery = ((options, queryClient) => {
  const isFocused = useOptionalIsFocused(options.subscribed);
  return useQueryFromTanstackReactQuery(
    withNavigationFocusSubscription(options, isFocused),
    queryClient,
  );
}) as typeof useQueryFromTanstackReactQuery;

export const useQueries: typeof useQueriesFromTanstackReactQuery = ((options, queryClient) => {
  const isFocused = useOptionalIsFocused(options.subscribed);
  return useQueriesFromTanstackReactQuery(
    withNavigationFocusSubscription(options, isFocused),
    queryClient,
  );
}) as typeof useQueriesFromTanstackReactQuery;

export const useInfiniteQuery: typeof useInfiniteQueryFromTanstackReactQuery = ((
  options,
  queryClient,
) => {
  const isFocused = useOptionalIsFocused(options.subscribed);
  return useInfiniteQueryFromTanstackReactQuery(
    withNavigationFocusSubscription(options, isFocused),
    queryClient,
  );
}) as typeof useInfiniteQueryFromTanstackReactQuery;

export const useSuspenseInfiniteQuery: typeof useSuspenseInfiniteQueryFromTanstackReactQuery = ((
  options,
  queryClient,
) => {
  const isFocused = useOptionalIsFocused(options.subscribed);
  return useSuspenseInfiniteQueryFromTanstackReactQuery(
    withNavigationFocusSubscription(options, isFocused),
    queryClient,
  );
}) as typeof useSuspenseInfiniteQueryFromTanstackReactQuery;
